import { Button } from '@/components/ui/button'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import NewTeamModal from '../../../TeamDashboard/components/NewTeamModal'
import { useState } from 'react'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { useAuth } from 'app/frontend/base/config/context/auth'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Section from 'app/frontend/base/components/Section'

const GuildTeamCard = ({ team }: { team: wowaudit.TeamReference }) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()

  const ctaButton = (
    <Button
      variant='outline'
      size='sm'
      className={
        accessLevel === 'none'
          ? 'cursor-not-allowed opacity-50 hover:bg-unset'
          : ''
      }
    >
      {t('shared.view')}
    </Button>
  )

  return (
    <div
      className='bg-muted rounded-md py-3 px-4 flex items-start justify-between gap-6 flex-wrap'
      key={team.id}
    >
      <div>
        {accessLevel === 'none' ? (
          <h3 className='font-semibold text-lg'>{team.name}</h3>
        ) : (
          <Link to={team.path}>
            <h3 className='font-semibold text-lg'>{team.name}</h3>
          </Link>
        )}
        <p
          className={
            team.description ? 'whitespace-pre-wrap' : 'opacity-50 italic'
          }
        >
          {team.description || t('shared.no-description')}
        </p>
      </div>

      <div className='flex gap-4 items-center justify-between whitespace-nowrap'>
        <span>{team.characterAmount} members</span>
        {accessLevel === 'none' ? (
          <Tooltip>
            <TooltipTrigger className='cursor-not-allowed' asChild>
              {ctaButton}
            </TooltipTrigger>
            <TooltipContent side='left'>
              {t('dashboard.guild.no-team-access')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Link to={team.path}>{ctaButton}</Link>
        )}
      </div>
    </div>
  )
}

const GuildTeams = () => {
  const { guild } = useGuild()
  const { t } = useTranslation()
  const [creatingTeam, setCreatingTeam] = useState(false)

  const sortedTeams = (guild?.teamReferences || []).sort(
    (a, b) => a.orderInGuild - b.orderInGuild,
  )

  return (
    <Section title={`Teams | ${guild?.name}`}>
      <h2 className='flex justify-between items-center'>
        <span>{t('shared.teams')}</span>
        <ProtectedContent
          section='base'
          requirement='admin'
          renderUnauthorized={false}
          requireAllTeams={true}
        >
          <Button size='sm' onClick={() => setCreatingTeam(true)}>
            {t('shared.create-new')}
          </Button>
        </ProtectedContent>
      </h2>

      {guild ? (
        <NewTeamModal setOpen={setCreatingTeam} open={creatingTeam} />
      ) : null}

      <div className='flex flex-col gap-2 text-sm'>
        {sortedTeams.map((team) => (
          <ProtectedContent
            key={team.id}
            section='base'
            requirement='member'
            teamReference={team}
            alwaysRenderContent={true}
          >
            <GuildTeamCard team={team} />
          </ProtectedContent>
        ))}
      </div>
    </Section>
  )
}

export default GuildTeams
